// Shared low-level constants and helpers for the E2E layer.
//
// Lives in its own file so that `envelope.ts`, `encrypt.ts`, and
// `decrypt.ts` all reach for the same names without cyclic imports.
// Nothing in this file is part of the public API of `@link/crypto`.
//
// _Requirements: 8.2, 11.5, 15.3, 15.4_

// ---------------------------------------------------------------------------
// Envelope wire constants
// ---------------------------------------------------------------------------

/** Envelope schema version emitted by `encryptE2e`. */
export const E2E_VERSION = 1 as const;

/** Algorithm tag emitted by `encryptE2e`. */
export const E2E_ALG = "ecdh-p256+hkdf+aes-256-gcm" as const;

/**
 * Fixed key order for top-level envelope JSON emission. Picked to be
 * small-then-large for human readability when an envelope is tail-cut
 * for log inspection. The set MUST be exhaustive for the typed
 * envelope; tests assert this.
 */
export const ENVELOPE_KEY_ORDER = [
  "v",
  "alg",
  "senderEphPub",
  "recipients",
  "ct",
  "iv",
  "aad",
] as const;

/** Fixed key order for the per-recipient JSON object. */
export const RECIPIENT_KEY_ORDER = [
  "accountIdHash",
  "wrappedKey",
  "iv",
] as const;

// ---------------------------------------------------------------------------
// Cryptographic constants
// ---------------------------------------------------------------------------

/** Bytes in an uncompressed SEC1 P-256 point (`0x04 || X || Y`). */
export const SEC1_UNCOMPRESSED_LENGTH = 65;

/** Bytes in an ECDH-P256 shared secret (the X coordinate). */
export const ECDH_SHARED_SECRET_BYTES = 32;

/** Bytes in `MK_msg` — AES-256 raw key. */
export const MK_BYTES = 32;

/** Bytes in an AES-GCM authentication tag in this codebase (always 128 bits). */
export const GCM_TAG_BYTES = 16;

/** AES-GCM nonce size used for content encryption (12 bytes per NIST SP 800-38D). */
export const CONTENT_IV_BYTES = 12;

/** AES-GCM nonce size used for the per-recipient wrap. */
export const WRAP_IV_BYTES = 12;

/**
 * AAD prefix bound into the content AES-GCM call. The full AAD also
 * concatenates UTF-8 channel/message IDs (see `buildE2eAad`). The
 * `.v1` segment is part of the domain separation: a future v2 envelope
 * with a different AAD layout cannot collide with a v1 ciphertext.
 */
export const E2E_AAD_PREFIX = new TextEncoder().encode("link.e2e.v1");

/**
 * Fixed HKDF salt input. The actual HKDF salt is `SHA-256` of this
 * string, mirroring the identity-derivation salt construction in
 * `derive-identity-key.ts`. The `.wrap` segment domain-separates this
 * HKDF use from any future identity-key HKDF use that lands in the
 * same package.
 */
export const HKDF_SALT_INPUT = "link.e2e.v1.wrap.salt";

/**
 * HKDF info prefix bound into the per-recipient wrap-key derivation.
 * The full info bytes also include `senderEphPub` (65) and the
 * recipient's `accountIdHash` (32) so each (recipient, message) gets
 * a unique wrap key, even before the per-recipient ECDH branch
 * differentiates the input keying material:
 *
 *   info = HKDF_INFO_PREFIX || senderEphPub || accountIdHash
 *
 * Including the recipient hash is belt-and-suspenders against an
 * implementation bug that would somehow produce the same ECDH shared
 * secret for two recipients. Including `senderEphPub` binds the wrap
 * key to the specific message that produced this envelope.
 */
export const HKDF_INFO_PREFIX = new TextEncoder().encode("link.e2e.v1.wrap");

// ---------------------------------------------------------------------------
// Web Crypto helpers
// ---------------------------------------------------------------------------

export function getSubtle(): SubtleCrypto {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) {
    throw new Error(
      "Web Crypto subtle is not available in this runtime. " +
        "encryptE2e/decryptE2e require a Web Crypto-capable " +
        "environment (modern browsers, Node 20+, or Bun).",
    );
  }
  return subtle;
}

/**
 * Compute the HKDF-SHA256 wrap key for a given (sender ephemeral
 * public key, recipient account-id-hash, ECDH shared secret) triple.
 *
 *   salt = SHA-256(HKDF_SALT_INPUT)
 *   info = HKDF_INFO_PREFIX || senderEphPub || accountIdHash
 *   key  = HKDF(ikm = sharedSecret, salt, info, length = 32)
 *
 * Returns an AES-GCM `CryptoKey` directly (non-extractable, encrypt +
 * decrypt usage) so the caller never sees the raw 32 bytes.
 */
export async function deriveWrapKey(
  sharedSecret: Uint8Array,
  senderEphPub: Uint8Array,
  accountIdHash: Uint8Array,
): Promise<CryptoKey> {
  if (sharedSecret.length !== ECDH_SHARED_SECRET_BYTES) {
    throw new Error(
      `deriveWrapKey: expected ${ECDH_SHARED_SECRET_BYTES}-byte shared secret, got ${sharedSecret.length}`,
    );
  }
  const subtle = getSubtle();

  const salt = new Uint8Array(
    await subtle.digest("SHA-256", new TextEncoder().encode(HKDF_SALT_INPUT)),
  );

  const info = new Uint8Array(
    HKDF_INFO_PREFIX.length + senderEphPub.length + accountIdHash.length,
  );
  info.set(HKDF_INFO_PREFIX, 0);
  info.set(senderEphPub, HKDF_INFO_PREFIX.length);
  info.set(accountIdHash, HKDF_INFO_PREFIX.length + senderEphPub.length);

  // Pass a fresh copy of `sharedSecret` to keep Web Crypto from
  // aliasing the caller's buffer (we wipe the original after use).
  const ikm = await subtle.importKey(
    "raw",
    sharedSecret.slice(),
    { name: "HKDF" },
    false,
    ["deriveKey"],
  );

  return subtle.deriveKey(
    { name: "HKDF", hash: "SHA-256", salt, info },
    ikm,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
}

/**
 * Validate that a candidate raw P-256 public key is the right shape
 * for ECDH. Throws on bad input. Web Crypto's `importKey` does the
 * actual on-curve check, so this is purely cheap framing validation.
 */
export function assertSec1UncompressedP256(
  name: string,
  raw: Uint8Array,
): void {
  if (raw.length !== SEC1_UNCOMPRESSED_LENGTH) {
    throw new Error(
      `${name}: expected ${SEC1_UNCOMPRESSED_LENGTH}-byte uncompressed SEC1 point, got ${raw.length}`,
    );
  }
  if (raw[0] !== 0x04) {
    throw new Error(
      `${name}: SEC1 uncompressed tag must be 0x04, got 0x${raw[0]!.toString(16)}`,
    );
  }
}

/**
 * Constant-time equality on two equal-length `Uint8Array`s. Used to
 * match the recipient `accountIdHash` in `decryptE2e` without leaking
 * a comparison-based timing oracle on the (otherwise public) hash.
 *
 * Returns `false` on length mismatch.
 */
export function ctEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) {
    diff |= (a[i]! ^ b[i]!);
  }
  return diff === 0;
}

/** Best-effort scrub of a byte buffer; the engine may keep a copy. */
export function wipe(buf: Uint8Array): void {
  buf.fill(0);
}
